import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import { FeatureFlags, RESOURCE_TO_FLAG_MAP } from '../constants/featureFlags';
import logger from '../utils/logger';
/**
 * Hook to manage API configuration based on feature flags
 * Simplified version - only switches base URLs since V1/V2 have identical endpoints
 * @returns {Object} API configuration utilities
 */
export const useApiConfig = () => {
  // Environment URLs
  const V1_API_URL = import.meta.env.VITE_API_URL;
  const V2_API_URL = import.meta.env.VITE_API_V2_URL;

  logger.log('🔧 useApiConfig hook called');
  logger.log('📡 V1_API_URL:', V1_API_URL);
  logger.log('📡 V2_API_URL:', V2_API_URL);

  // Get GrowthBook instance to check if it's ready
  const growthbook = useGrowthBook();
  const isReady = growthbook?.ready || false;

  // Global controls
  const globalOverride = useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_GLOBAL_OVERRIDE);
  const debugMode = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_API_DEBUG_MODE);

  // Resource-specific flags - using the constants
  const apiVersionConfig = {
    users: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_USERS),
    roles: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_ROLES),
    organizations: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_ORGANIZATIONS),
    codes: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_CODES),
    queues: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_QUEUES),
    calls: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_CALLS),
    versions: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_VERSIONS),
    cms1500Jobs: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_CMS1500_JOBS),
    dashboard: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_DASHBOARD),
    leadImports: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_LEAD_IMPORTS),
    leads: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_LEADS),
    states: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_STATES),
    permissionTemplates: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_PERMISSION_TEMPLATES),
    insurancePlans: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_INSURANCE_PLANS),
    impersonation: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_IMPERSONATION),
    'product-features': useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_PRODUCT_FEATURES),
    'partner-marketing-assets': useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_PARTNER_MARKETING_ASSETS),

    // Additional resources that may exist in the admin panel
    hotline: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_CALLS), // hotline uses same as calls
    chats: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_CALLS), // chats uses same as calls
    contentLinks: useFeatureIsOn(FeatureFlags.USE_ADMIN_PANEL_API_V2_CODES) // content links can use codes flag
  };

  /**
   * Get API URL for a specific resource
   * @param {string} resource - Resource name
   * @returns {string} API base URL
   */
  const getApiUrl = (resource) => {
    // If GrowthBook isn't ready, default to V1 for safety
    if (!isReady) {
      logger.log('⚠️ GrowthBook not ready, defaulting to V1 API');
      return V1_API_URL;
    }

    // Global override forces V1 for emergency rollback
    if (globalOverride) {
      logger.log('🚨 Global override is enabled, using V1 API URL');
      return V1_API_URL;
    }

    const useV2 = apiVersionConfig[resource];
    const selectedUrl = useV2 ? V2_API_URL : V1_API_URL;

    logger.log(`🎯 ${resource}: flag=${useV2}, using ${useV2 ? 'V2' : 'V1'} - ${selectedUrl}`);

    // Debug logging only when debug mode is enabled
    if (debugMode) {
      logger.log(`[API Config] ${resource}: using ${useV2 ? 'V2' : 'V1'} - ${selectedUrl}`);
    }

    return selectedUrl;
  };

  /**
   * Get API version for a resource
   * @param {string} resource - Resource name
   * @returns {string} 'v1' or 'v2'
   */
  const getApiVersion = (resource) => {
    if (globalOverride) return 'v1';
    return apiVersionConfig[resource] ? 'v2' : 'v1';
  };

  /**
   * Get feature flag name for a resource
   * @param {string} resource - Resource name
   * @returns {string|null} Feature flag name
   */
  const getFlagForResource = (resource) => {
    return RESOURCE_TO_FLAG_MAP[resource] || null;
  };

  /**
   * Check if any V2 flags are enabled (for monitoring)
   * @returns {boolean}
   */
  const isAnyV2Enabled = () => {
    return Object.values(apiVersionConfig).some(Boolean);
  };

  /**
   * Get migration status summary
   * @returns {Object} Summary of migration status
   */
  const getMigrationStatus = () => {
    const total = Object.keys(apiVersionConfig).length;
    const v2Count = Object.values(apiVersionConfig).filter(Boolean).length;

    return {
      total,
      v2Count,
      v1Count: total - v2Count,
      percentage: Math.round((v2Count / total) * 100),
      isComplete: v2Count === total,
      globalOverride
    };
  };

  /**
   * Create a proxy for the import.meta.env.VITE_API_URL
   * that returns the feature flag controlled URL
   * This allows existing data providers to work without modification
   * @param {string} resource - Resource name to determine which URL to use
   * @returns {string} The appropriate API URL
   */
  const getApiUrlForResource = (resource) => {
    return getApiUrl(resource);
  };

  return {
    // Core functions
    getApiUrl,
    getApiVersion,
    getFlagForResource,
    getApiUrlForResource,

    // Status and monitoring
    isAnyV2Enabled,
    getMigrationStatus,

    // Configuration
    apiVersionConfig,
    V1_API_URL,
    V2_API_URL,

    // Debug information
    debugMode,
    globalOverride
  };
};

/**
 * Higher-order component to inject API configuration
 * @param {React.Component} WrappedComponent
 * @returns {React.Component}
 */
export const withApiConfig = (WrappedComponent) => {
  return function WithApiConfigComponent(props) {
    const apiConfig = useApiConfig();
    return <WrappedComponent {...props} apiConfig={apiConfig} />;
  };
};
